import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import { statusBadge } from './employeePortalShared';
import { formatPortalDate, formatPortalMoney } from './employeePortalSaudiFormat';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'performancePage';
const API = '/api/hr/evaluations';

const EMPTY = {
  period: '', evaluator: '', overallScore: '', status: '', bonusAmount: '',
  performance: '', competences: '', behaviour: '', objectives: '',
  strengths: '', improvements: '', comments: '', reviewDate: '',
};

const PERF_STATUS = [
  { value: 'termine', labelKey: 'stat_completed' },
  { value: 'en_cours', labelKey: 'stat_pending' },
];

function parseScore(record) {
  const raw = record?.overallScore ?? record?.noteGlobale ?? record?.score;
  const n = parseFloat(raw);
  return Number.isNaN(n) ? null : n;
}

function derivePerfStatus(record) {
  if (record?.status) return record.status;
  const score = parseScore(record);
  if (score == null) return 'en_cours';
  return score >= 3.5 ? 'termine' : 'en_cours';
}

function computePerformanceStats(records) {
  const withStatus = records.map((r) => ({ ...r, status: derivePerfStatus(r) }));
  const scores = records.map(parseScore).filter((s) => s != null);
  const avgScore = scores.length
    ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2)
    : '—';

  return [
    { titleKey: 'stat_total', value: records.length, icon: Star, color: 'from-teal-500 to-teal-600', variant: 'copper' },
    {
      titleKey: 'stat_completed',
      value: withStatus.filter((r) => r.status === 'termine').length,
      icon: CheckCircle,
      color: 'from-green-500 to-green-600',
      variant: 'forest',
    },
    {
      titleKey: 'stat_pending',
      value: withStatus.filter((r) => r.status === 'en_cours').length,
      icon: Clock,
      color: 'from-amber-500 to-amber-600',
      variant: 'forest',
    },
    {
      titleKey: 'stat_avg_score',
      value: avgScore,
      icon: TrendingUp,
      color: 'from-blue-500 to-blue-600',
      variant: 'forest',
    },
  ];
}

export default function EmployeePortalPerformancePage() {
  const { i18n } = useTranslation();
  const { employeeId } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => (employeeId ? { employee_id: employeeId } : {}), [employeeId]);

  const renderBonus = (r) => {
    const bonus = r.bonusAmount ?? r.bonus ?? r.performanceBonus;
    return bonus != null && bonus !== '' ? formatPortalMoney(bonus) : '—';
  };

  const renderScore = (r) => {
    const score = parseScore(r);
    return score != null ? score.toFixed(2) : '—';
  };

  const renderReviewDate = (r) => formatPortalDate(r.reviewDate || r.date || r.evaluationDate, i18n.language);

  const mapRecordToForm = (r) => {
    const score = parseScore(r);
    const bonus = r.bonusAmount ?? r.bonus ?? r.performanceBonus;
    return {
      period: r.period || r.evaluationPeriod || '',
      evaluator: r.evaluator || r.evaluatorName || '',
      overallScore: score != null ? score.toFixed(2) : '',
      status: derivePerfStatus(r),
      performance: r.performance != null ? String(r.performance) : '',
      competences: r.competences != null ? String(r.competences) : '',
      behaviour: (r.behaviour ?? r.comportement) != null ? String(r.behaviour ?? r.comportement) : '',
      objectives: (r.objectives ?? r.objectifs) != null ? String(r.objectives ?? r.objectifs) : '',
      strengths: r.strengths || '',
      improvements: r.improvements || r.areasForImprovement || r.weaknesses || '',
      comments: r.comments || '',
      reviewDate: formatPortalDate(r.reviewDate || r.date || r.evaluationDate, i18n.language),
      bonusAmount: bonus != null && bonus !== '' ? formatPortalMoney(bonus) : '',
    };
  };

  return (
    <EmployeePortalCrudShell
      ns={NS}
      icon={Star}
      apiPath={API}
      apiQueryParams={apiQueryParams}
      requireEmployeeScope
      allowCreate={false}
      allowEdit={false}
      allowDelete={false}
      gradient="from-amber-500 to-amber-700"
      emptyForm={EMPTY}
      mapRecordToForm={mapRecordToForm}
      computeStats={computePerformanceStats}
      columns={[
        { key: 'period', labelKey: 'col_period' },
        { key: 'evaluator', labelKey: 'col_evaluator' },
        { key: 'overallScore', labelKey: 'col_score', render: renderScore },
        { key: 'bonusAmount', labelKey: 'col_bonus', render: renderBonus },
        { key: 'reviewDate', labelKey: 'col_review_date', render: renderReviewDate },
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(derivePerfStatus(r), pl, PERF_STATUS) },
      ]}
      formSections={[
        {
          id: 'summary',
          titleKey: 'section_summary',
          fields: [
            { key: 'period', labelKey: 'col_period', readOnly: true },
            { key: 'reviewDate', labelKey: 'col_review_date', readOnly: true },
            { key: 'evaluator', labelKey: 'col_evaluator', readOnly: true },
            { key: 'overallScore', labelKey: 'col_score', readOnly: true },
            { key: 'bonusAmount', labelKey: 'col_bonus', readOnly: true },
            { key: 'status', labelKey: 'col_status', readOnly: true },
          ],
        },
        {
          id: 'scores',
          titleKey: 'section_scores',
          fields: [
            { key: 'performance', labelKey: 'field_performance', readOnly: true },
            { key: 'competences', labelKey: 'field_competences', readOnly: true },
            { key: 'behaviour', labelKey: 'field_behaviour', readOnly: true },
            { key: 'objectives', labelKey: 'field_objectives', readOnly: true },
          ],
        },
        {
          id: 'feedback',
          titleKey: 'section_feedback',
          fields: [
            { key: 'strengths', labelKey: 'field_strengths', type: 'textarea', readOnly: true },
            { key: 'improvements', labelKey: 'field_improvements', type: 'textarea', readOnly: true },
            { key: 'comments', labelKey: 'field_comments', type: 'textarea', readOnly: true },
          ],
        },
      ]}
    />
  );
}
